import type { BugsResponse, BugComment, BugDetail, TrackerResult } from "./types";

export const TRACKER_ERROR_MESSAGES: Record<number, string> = {
  400: "Saisie invalide — vérifiez votre message.",
  401: "Configuration du widget invalide — contactez l'agence.",
  403: "Ce domaine n'est pas autorisé pour ce projet — contactez l'agence.",
  404: "Ce bug n'existe plus.",
  429: "Trop de requêtes, réessayez dans un moment.",
};

export const TRACKER_NETWORK_MESSAGE =
  "Chargement impossible. Vérifiez la connexion et réessayez.";

/** Message d'erreur du serveur si le corps en contient un, sinon le générique. */
async function errorResult<T>(response: Response): Promise<TrackerResult<T>> {
  let message = TRACKER_ERROR_MESSAGES[response.status] ?? TRACKER_NETWORK_MESSAGE;
  // 400 (validation) et 409 (conflit de statut) portent un message métier à
  // afficher tel quel.
  if (response.status === 400 || response.status === 409) {
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // corps absent — message par défaut
    }
  }
  return { ok: false, status: response.status, message };
}

async function get<T>(url: string, apiKey: string): Promise<TrackerResult<T>> {
  let response: Response;
  try {
    response = await fetch(url, { headers: { "X-Api-Key": apiKey } });
  } catch {
    return { ok: false, status: 0, message: TRACKER_NETWORK_MESSAGE };
  }
  if (!response.ok) return errorResult(response);
  try {
    return { ok: true, data: (await response.json()) as T };
  } catch {
    return { ok: false, status: 0, message: TRACKER_NETWORK_MESSAGE };
  }
}

async function post<T>(
  url: string,
  apiKey: string,
  body?: unknown,
): Promise<TrackerResult<T>> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "X-Api-Key": apiKey,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    return { ok: false, status: 0, message: TRACKER_NETWORK_MESSAGE };
  }
  if (!response.ok) return errorResult(response);
  try {
    return { ok: true, data: (await response.json()) as T };
  } catch {
    return { ok: false, status: 0, message: TRACKER_NETWORK_MESSAGE };
  }
}

const bugUrl = (trackerBase: string, bugId: string): string =>
  `${trackerBase}/api/widget/bugs/${encodeURIComponent(bugId)}`;

/** GET /api/widget/bugs — projet, statuts (ordre + libellés) et liste des bugs. */
export function fetchBugs(trackerBase: string, apiKey: string): Promise<TrackerResult<BugsResponse>> {
  return get<BugsResponse>(`${trackerBase}/api/widget/bugs`, apiKey);
}

/** GET /api/widget/bugs/[bugId] — détail avec commentaires et timeline. */
export function fetchBugDetail(
  trackerBase: string,
  apiKey: string,
  bugId: string,
): Promise<TrackerResult<BugDetail>> {
  return get<BugDetail>(bugUrl(trackerBase, bugId), apiKey);
}

/**
 * POST /api/widget/bugs/[bugId]/validate — validation client, sans body.
 * 409 = le statut a changé entre-temps : afficher le message serveur et recharger.
 */
export function validateBug(
  trackerBase: string,
  apiKey: string,
  bugId: string,
): Promise<TrackerResult<{ id: string; status: string }>> {
  return post<{ id: string; status: string }>(
    `${bugUrl(trackerBase, bugId)}/validate`,
    apiKey,
  );
}

/**
 * POST /api/widget/bugs/[bugId]/reject — refus d'un correctif, commentaire
 * d'explication obligatoire. Mêmes conflits 409 que la validation.
 */
export function rejectBug(
  trackerBase: string,
  apiKey: string,
  bugId: string,
  comment: string,
  authorName?: string,
): Promise<TrackerResult<{ id: string; status: string }>> {
  return post<{ id: string; status: string }>(
    `${bugUrl(trackerBase, bugId)}/reject`,
    apiKey,
    { comment, ...(authorName ? { authorName } : {}) },
  );
}

/** POST /api/widget/bugs/[bugId]/comments — commentaire du client. */
export function postBugComment(
  trackerBase: string,
  apiKey: string,
  bugId: string,
  content: string,
  authorName?: string,
): Promise<TrackerResult<BugComment>> {
  return post<BugComment>(
    `${bugUrl(trackerBase, bugId)}/comments`,
    apiKey,
    { content, ...(authorName ? { authorName } : {}) },
  );
}
